import { useMemo, useRef, useState } from 'react';
import { Check, ShieldCheck } from 'lucide-react';
import SignaturePad from '@/components/SignaturePad';
import PncFooter from '@/components/PncFooter';
import { apiPublic } from '@/lib/api';
import {
  MEDICAL_QUESTIONS,
  HAVS_QUESTIONS,
  MEDICAL_CONFIDENTIALITY_NOTE,
  MEDICAL_DECLARATION,
} from '@/data/medicalHavsContent';

const ALL_QUESTIONS = [...MEDICAL_QUESTIONS, ...HAVS_QUESTIONS];

function YesNoRow({ q, index, value, onChange }) {
  return (
    <div
      data-testid="medical-question-row"
      data-question-id={q.id}
      className={`flex items-center justify-between gap-4 px-4 py-3 rounded-md border transition-colors ${
        value ? 'border-[var(--line)] bg-[#161616]' : 'border-[var(--line)] hover:border-[#4A4A4A]'
      }`}
    >
      <p className="text-sm leading-relaxed text-[#E5E5E5]">
        <span className="text-xs font-bold text-[var(--muted)] mr-2">{String(index).padStart(2, '0')}</span>
        {q.text}
      </p>
      <div className="flex gap-2 shrink-0">
        {['yes', 'no'].map((v) => (
          <label
            key={v}
            data-testid={`medical-answer-${q.id}-${v}`}
            className={`flex items-center justify-center px-4 py-1.5 rounded-md border cursor-pointer text-xs font-bold uppercase tracking-wider transition-colors ${
              value === v
                ? v === 'yes'
                  ? 'border-rose-500 bg-rose-500/10 text-rose-300'
                  : 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                : 'border-[var(--line)] text-[var(--muted)] hover:border-[#4A4A4A]'
            }`}
          >
            <input
              type="radio"
              name={`q_${q.id}`}
              value={v}
              checked={value === v}
              onChange={() => onChange(q.id, v)}
              className="sr-only"
            />
            {v}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function MedicalHavsForm({ token, meta }) {
  const company = meta.contract_type === 'unique' ? 'PNC Unique LTD' : 'PNC Smart Solutions LTD';
  const [answers, setAnswers] = useState({});
  const [workerName, setWorkerName] = useState(meta.contractor_name || '');
  const [workerCompany, setWorkerCompany] = useState(meta.contractor_company || '');
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const sigRef = useRef(null);

  const todayDisplay = useMemo(() => new Date().toLocaleDateString('en-GB'), []);
  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === ALL_QUESTIONS.length;
  const anyYes = Object.values(answers).some((v) => v === 'yes');
  const detailsMissing = anyYes && !additionalDetails.trim();

  const setAnswer = (id, v) => {
    setAnswers((prev) => ({ ...prev, [id]: v }));
    setError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!allAnswered) {
      setError(`Please answer all questions — ${ALL_QUESTIONS.length - answeredCount} still unanswered.`);
      return;
    }
    if (!workerName.trim()) { setError('Please enter your full name.'); return; }
    if (!workerCompany.trim()) { setError('Please enter your company name.'); return; }
    if (detailsMissing) {
      setError('Please provide details for your Yes answer(s).');
      return;
    }
    if (!confirmed) { setError('Please tick the box to confirm your answers are accurate.'); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError('Please draw your signature before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await apiPublic.post(`/contracts/sign/${token}/submit-medical`, {
        worker_name: workerName.trim(),
        worker_company: workerCompany.trim(),
        signature_png: sigRef.current.toDataURL(),
        declaration_confirmed: true,
        answers,
        additional_details: additionalDetails.trim(),
      });
      setDone(r.data);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grain flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-16">
          <div data-testid="medical-success" className="max-w-lg w-full pnc-card rounded-lg p-10 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mb-4">
              <Check size={22} strokeWidth={2.5} />
            </div>
            <h1 className="serif text-3xl mb-2">Questionnaire submitted</h1>
            <p className="text-sm text-[var(--muted)] mb-4">
              Thank you, {workerName}. Your <strong className="text-[var(--text)]">Medical History &amp; HAVS</strong>{' '}
              questionnaire has been securely delivered to PNC.
            </p>
            <p className="text-xs text-[#8A8A8A] inline-flex items-start gap-2 text-left">
              <ShieldCheck size={14} strokeWidth={2} className="shrink-0 mt-0.5 text-emerald-400" />
              Your answers are confidential and visible only to authorised PNC HR and site management staff.
            </p>
          </div>
        </div>
        <PncFooter />
      </div>
    );
  }

  return (
    <div data-testid="medical-form-page" className="min-h-screen grain">
      <header className="relative border-b border-[var(--line)] bg-[#0A0A0A]/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/pnc-logo-white.png" alt="PNC UNIQUE LTD" className="h-9 w-auto" />
          <span className="text-xs text-[var(--muted)]">Secure &amp; confidential</span>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 py-12">
        <p className="field-label mb-2">Confidential questionnaire</p>
        <h1 className="serif text-4xl leading-tight mb-1">Medical History &amp; HAVS</h1>
        <p className="text-sm text-[var(--muted)] mb-1">{company}</p>
        <p className="text-sm text-[var(--muted)] mb-8">
          Sent to <strong>{meta.employee_email}</strong>
          {meta.expires_at ? <> &middot; expires {new Date(meta.expires_at).toLocaleDateString()}</> : null}
        </p>

        <form data-testid="medical-form" onSubmit={submit} className="space-y-8">
          {/* Section 1 */}
          <section className="pnc-card rounded-lg p-7 space-y-4">
            <div>
              <p className="serif text-2xl leading-snug mb-1">Medical History</p>
              <p className="text-sm font-semibold text-[#E5E5E5] mb-1">
                Please answer honestly. This is confidential.
              </p>
              <p className="text-xs text-[var(--muted)] leading-relaxed">{MEDICAL_CONFIDENTIALITY_NOTE}</p>
            </div>
            <div data-testid="medical-history-list" className="space-y-2">
              {MEDICAL_QUESTIONS.map((q, i) => (
                <YesNoRow key={q.id} q={q} index={i + 1} value={answers[q.id]} onChange={setAnswer} />
              ))}
            </div>
          </section>

          {/* Section 2 */}
          <section className="pnc-card rounded-lg p-7 space-y-4">
            <div>
              <p className="serif text-2xl leading-snug mb-1">HAVS Questionnaire</p>
              <p className="text-xs text-[var(--muted)]">Hand-arm vibration screening</p>
            </div>
            <div data-testid="havs-list" className="space-y-2">
              {HAVS_QUESTIONS.map((q, i) => (
                <YesNoRow key={q.id} q={q} index={i + 1} value={answers[q.id]} onChange={setAnswer} />
              ))}
            </div>
          </section>

          {/* Additional details */}
          <section className="pnc-card rounded-lg p-7 space-y-4">
            <div>
              <p className="serif text-2xl leading-snug mb-1">
                Additional Details
                {anyYes && <span className="text-red-500 text-base align-top ml-1">*</span>}
              </p>
              <p className="text-sm text-[#E5E5E5]">
                If you answered <strong>YES</strong> to any of the questions above, please provide
                further details below.
              </p>
            </div>
            <textarea
              data-testid="medical-additional-details"
              value={additionalDetails}
              onChange={(e) => { setAdditionalDetails(e.target.value); setError(''); }}
              placeholder={anyYes
                ? 'Please explain your Yes answer(s) — required'
                : 'Optional — nothing to add if all your answers are No'}
              rows={6}
              maxLength={5000}
              className="input-bare leading-relaxed"
            />
            {detailsMissing && (
              <p data-testid="medical-details-hint" className="text-xs text-amber-300">
                You answered Yes to at least one question — please provide details before submitting.
              </p>
            )}
          </section>

          {/* Declaration & signature */}
          <section className="pnc-card rounded-lg p-7 space-y-5">
            <div className="bg-[var(--paper-deep)] border border-[var(--line)] rounded-md p-5">
              <p className="text-xs uppercase tracking-wider text-[var(--muted)] mb-2">Declaration</p>
              <p className="serif text-xl leading-snug">{MEDICAL_DECLARATION}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="field-label block mb-1.5">
                  Full name <span className="text-red-500">*</span>
                </label>
                <input
                  data-testid="medical-worker-name"
                  value={workerName}
                  onChange={(e) => { setWorkerName(e.target.value); setError(''); }}
                  placeholder="Your full name"
                  className="input-bare"
                  maxLength={200}
                />
              </div>
              <div>
                <label className="field-label block mb-1.5">
                  Company name <span className="text-red-500">*</span>
                </label>
                <input
                  data-testid="medical-worker-company"
                  value={workerCompany}
                  onChange={(e) => { setWorkerCompany(e.target.value); setError(''); }}
                  placeholder="Your company"
                  className="input-bare"
                  maxLength={200}
                />
              </div>
            </div>

            <div>
              <label className="field-label block mb-1.5">Date</label>
              <input
                data-testid="medical-date"
                value={todayDisplay}
                readOnly
                className="input-bare opacity-70 cursor-default"
              />
            </div>

            <div>
              <label className="field-label block mb-1.5">
                Your signature <span className="text-red-500">*</span>
              </label>
              <SignaturePad ref={sigRef} />
            </div>

            <label className="flex items-start gap-2.5 text-sm text-[#D4D4D4] cursor-pointer">
              <input
                data-testid="medical-confirm-checkbox"
                type="checkbox"
                checked={confirmed}
                onChange={(e) => { setConfirmed(e.target.checked); setError(''); }}
                className="accent-[var(--silver)] mt-0.5"
              />
              <span>I confirm that the information supplied above is accurate.</span>
            </label>

            <div
              data-testid="medical-progress"
              className={`text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-md border inline-block ${
                allAnswered
                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                  : 'border-[var(--line)] bg-[var(--paper-deep)] text-[#D4D4D4]'
              }`}
            >
              {answeredCount} of {ALL_QUESTIONS.length} questions answered
            </div>

            {error && (
              <div data-testid="medical-error" className="alert-error text-sm rounded p-3">{error}</div>
            )}

            <button
              data-testid="medical-submit"
              type="submit"
              disabled={submitting || !allAnswered || !confirmed || detailsMissing || !workerName.trim() || !workerCompany.trim()}
              className="btn-ink w-full py-3.5 rounded-md text-sm font-bold uppercase tracking-[0.08em] disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit questionnaire'}
            </button>
          </section>
        </form>
      </main>

      <PncFooter />
    </div>
  );
}
